import React, { ReactElement, ReactNode } from 'react';
import { ButtonBase, CircularProgress } from '@mui/material';
import { useReducedMotion } from '../../shared';
import { FEED_COLORS, FEED_FONT } from './feedTokens';

const TONE_STYLES = {
  gold: {
    color: FEED_COLORS.textPrimary,
    backgroundColor: FEED_COLORS.gold,
    hover: FEED_COLORS.goldHover,
    border: '1px solid transparent',
  },
  navy: {
    color: FEED_COLORS.inverse,
    backgroundColor: FEED_COLORS.navy,
    hover: FEED_COLORS.navyHover,
    border: '1px solid transparent',
  },
  tertiary: {
    color: FEED_COLORS.textPrimary,
    backgroundColor: FEED_COLORS.surface,
    hover: '#F6F8FB',
    border: '1px solid #D0D5DD',
  },
} as const;

export interface FeedButtonProps {
  label: string;
  onClick: () => void;
  tone?: 'gold' | 'navy' | 'tertiary';
  icon?: ReactNode;
  endIcon?: ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  width?: number;
  size?: 'default' | 'small';
}

export const FeedButton = ({
  label,
  onClick,
  tone = 'gold',
  icon,
  endIcon,
  loading = false,
  fullWidth = false,
  width,
  size = 'default',
}: FeedButtonProps): ReactElement => {
  const reduced = useReducedMotion();
  const compact = size === 'small';
  const toneStyle = TONE_STYLES[tone];

  return (
    <ButtonBase
      component="button"
      type="button"
      onClick={loading ? undefined : onClick}
      aria-busy={loading}
      disableRipple
      sx={{
        fontFamily: FEED_FONT,
        fontWeight: 500,
        fontSize: compact ? '12px' : '14px',
        lineHeight: compact ? '16px' : '22px',
        color: toneStyle.color,
        backgroundColor: toneStyle.backgroundColor,
        border: toneStyle.border,
        boxSizing: 'border-box',
        borderRadius: '8px',
        gap: compact ? '6px' : '8px',
        px: compact ? '14px' : '24px',
        py: compact ? '7px' : '11px',
        width: fullWidth ? '100%' : width ? `${width}px` : 'auto',
        minHeight: compact ? '32px' : '44px',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        cursor: loading ? 'progress' : 'pointer',
        transition: reduced ? 'none' : 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: toneStyle.hover,
        },
        '&:focus-visible': {
          outline: `2px solid ${FEED_COLORS.textPrimary}`,
          outlineOffset: '2px',
        },
      }}
    >
      {loading ? (
        <CircularProgress
          size={compact ? 16 : 18}
          thickness={5}
          sx={{ color: 'inherit' }}
        />
      ) : (
        icon
      )}
      {label}
      {endIcon}
    </ButtonBase>
  );
};
